/** @jsxImportSource solid-js */
import type { JSX } from "solid-js";
import { createSignal, onMount, Show } from "solid-js";
import type { SelectedTargets } from "../store/widget-store";
import { autosizeTextarea } from "../utils/autosize-textarea";
import { popIn } from "../utils/widget-motion";
import { CARD_TEXTAREA_CLASS, WidgetBubbleGrip } from "./widget-bubble-grip";
import { WidgetIcon } from "./widget-icon";
import { WidgetLinkedTargets } from "./widget-linked-targets";

export type WidgetComposerProps = {
  /** Captures + collects the feedback; resolves with whether it succeeded. */
  onSubmit: (comment: string) => Promise<boolean> | boolean;
  /** Close the composer after the success morph has had time to read. */
  onDone: () => void;
  /** Reports whether the draft has text so the store can protect it from stray page clicks. */
  onDirty?: (dirty: boolean) => void;
  /** Mirrors the raw draft text so unsent work can be persisted. */
  onDraft?: (text: string) => void;
  /** Prefill for a draft restored from persistence. */
  initialValue?: string;
  /** Grip pointerdown from the bubble shell; lets the card be dragged aside. */
  onDragStart?: (e: PointerEvent) => void;
  targets: SelectedTargets;
  onRemoveTarget: (index: number) => void;
};

/** How long the success checkmark holds before the bubble animates out. */
const SUCCESS_HOLD_MS = 160;

/** Figma-style comment bubble: a textarea and a submit arrow, nothing else. */
export function WidgetComposer(props: WidgetComposerProps): JSX.Element {
  const [phase, setPhase] = createSignal<"idle" | "busy" | "done">("idle");
  // Captured once: the store resets its draft signal while this instance is
  // still playing its exit animation, and a reactive value would flash the
  // restored text away mid-exit.
  const initialValue = props.initialValue ?? "";
  let textareaRef: HTMLTextAreaElement | undefined;

  onMount(() => {
    // A restored draft can span multiple lines; size to it up front.
    if (textareaRef?.value) autosizeTextarea(textareaRef);
    // Synchronous focus keeps iOS raising the soft keyboard (it only honors
    // focus inside the user gesture), but the page click that opened the
    // composer moves focus again after its handlers run - so also re-focus a
    // frame later to win that race on desktop.
    textareaRef?.focus({ preventScroll: true });
    requestAnimationFrame(() => textareaRef?.focus({ preventScroll: true }));
  });

  const submit = (): void => {
    void (async () => {
      const value = textareaRef?.value.trim() ?? "";
      if (!value) {
        textareaRef?.focus();
        return;
      }
      if (phase() !== "idle") return;
      setPhase("busy");
      let ok = false;
      try {
        ok = await props.onSubmit(value);
      } catch {
        ok = false;
      }
      if (!ok) {
        // Capture failed (store already toasted); let the user retry.
        setPhase("idle");
        return;
      }
      setPhase("done");
      await new Promise((resolve) => setTimeout(resolve, SUCCESS_HOLD_MS));
      props.onDone();
    })();
  };

  return (
    <form
      data-pl-composer=""
      class="text-pl-foreground shadow-pl rounded-[20px]"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div class="border-pl-border bg-pl-background relative z-10 overflow-hidden rounded-[20px] border shadow-[0_2px_8px_oklch(0.145_0.018_261/0.08)]">
        <textarea
          ref={textareaRef}
          class={`${CARD_TEXTAREA_CLASS} placeholder:text-pl-muted-foreground/60`}
          rows={1}
          placeholder="What's wrong here?"
          disabled={phase() !== "idle"}
          value={initialValue}
          onInput={(e) => {
            autosizeTextarea(e.currentTarget);
            props.onDirty?.(e.currentTarget.value.trim().length > 0);
            props.onDraft?.(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div class="flex items-center gap-2 p-1.5">
          <Show when={phase() === "busy"}>
            <span
              ref={(el) => popIn(el, { from: "translateY(2px)" })}
              class="text-pl-muted-foreground min-w-0 flex-1 truncate text-xs"
            >
              Taking screenshot…
            </span>
          </Show>
          <span class="flex-1" />
          <WidgetBubbleGrip onDragStart={props.onDragStart} />
          <button
            type="submit"
            data-pl-add=""
            aria-label={
              phase() === "done"
                ? "Feedback added"
                : phase() === "busy"
                  ? "Taking screenshot"
                  : "Add feedback"
            }
            disabled={phase() !== "idle"}
            classList={{
              "text-pl-primary-foreground grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-full border shadow-pl-circle transition-transform duration-[130ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.94] disabled:cursor-default": true,
              "border-pl-primary/80 bg-pl-gradient hover:bg-pl-gradient-hover active:bg-pl-gradient-active":
                phase() !== "done",
              "border-pl-success/80 bg-pl-success": phase() === "done",
            }}
          >
            <Show
              when={phase() === "idle"}
              fallback={
                <Show
                  when={phase() === "done"}
                  fallback={
                    <span class="animate-pl-spin block h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent" />
                  }
                >
                  <span
                    ref={(el) => popIn(el, { from: "scale(0.4)" })}
                    class="grid place-items-center"
                  >
                    <WidgetIcon name="check" size={14} strokeWidth={2.5} />
                  </span>
                </Show>
              }
            >
              <WidgetIcon name="arrowUp" size={15} />
            </Show>
          </button>
        </div>
      </div>
      <WidgetLinkedTargets targets={props.targets} onRemove={props.onRemoveTarget} />
    </form>
  );
}
